/*
 * Copyright (c) 2008-2022 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
 * This is CONFIDENTIAL code. Use is subject to license terms.
 */
import store from "@/store"
import { isFeatureLicensed, ensureLicenseLoaded } from "@/licensing/licenseValidation"

/* global log */

/**
 * guard to redirect authenticated users
 *
 * @param to
 * @param from
 * @param next
 */
const ifNotAuthenticated = (to, from, next) => {
  if (!store.getters["auth/isAuthenticated"]) {
    next()
    return
  }
  next({ name: "home" })
}

/**
 * Handles the logic to ensure the meeting permissions have been loaded and can be used
 * to validate against.
 *
 * @returns {Promise}
 */
const ensureMeetingPermissionsLoaded = function (meetingId) {
  return new Promise((resolve) => {
    if (
      store.getters["auth/meetingPermissionsLoaded"] &&
      store.getters["auth/getMeetingPermissions"](meetingId).length > 0
    ) {
      resolve()
      return
    }
    // Important in case of a breakout meeting:
    // Meeting permissions will be stored using the main meeting ID
    // Because of that we need to load the meeting (together with its parent ID) first
    store.dispatch("meetings/loadMeeting", meetingId).then(() => {
      store.dispatch("auth/loadMeetingPermissionsById", meetingId).then(() => {
        resolve()
      })
    })
  })
}

/**
 * Provides a function that can be used by a router guard to validate
 * if the provided permission is present.
 *
 * @param {String} permission
 * @returns {Function}
 */
const beforeRoutePermissionCheckGuard = function (permission) {
  return (...[to, , next]) => {
    ensureMeetingPermissionsLoaded(to.params.id).then(() => {
      if (!store.getters["auth/hasMeetingPermission"](to.params.id, permission)) {
        log.warn("Missing permission ", permission, ". Redirecting to homepage.")
        next({ name: "home" })
        return
      }
      log.debug(`Permission check passed: user has ${permission} permission.`)
      next()
    })
  }
}

/**
 * Provides a function that can be used by a router guard to validate if the provided license feature
 * is present on the effective license for the current tenant.
 *
 * @param {String} licenseFeature
 * @returns {Function}
 */
const beforeRouteLicenseCheckGuard = function (licenseFeature) {
  return (...[, , next]) => {
    // Fire license load method to validate if the required license information has been loaded.
    // If loaded proceed with license validation, if not, wait for the license information to be loaded
    ensureLicenseLoaded().then(() => {
      if (!licenseFeature || isFeatureLicensed(licenseFeature)) {
        log.debug(
          "License feature check passed: ",
          licenseFeature ? `user has ${licenseFeature} feature permission.` : " no license feature required."
        )
        next()
      } else {
        log.warn("Missing license feature ", licenseFeature, ". Redirecting to homepage.")
        next({ name: "home" })
      }
    })
  }
}

/**
 * Provides a function that can be used by a router guard to validate if the provided permission and
 * the provided license feature is present on the effective license for the current tenant.
 *
 * @param {String} licenseFeature
 * @param {String} permission
 * @returns {Function}
 */
const beforeRouteLicenseAndPermissionCheckGuard = function (licenseFeature, permission) {
  return (...[to, , next]) => {
    // Fire license load method to validate if the required license information has been loaded.
    // If loaded proceed with license validation, if not, wait for the license information to be loaded
    Promise.all([ensureLicenseLoaded(), ensureMeetingPermissionsLoaded(to.params.id)]).then(() => {
      if (!licenseFeature || isFeatureLicensed(licenseFeature)) {
        log.debug(
          "License feature check passed: ",
          licenseFeature ? `user has ${licenseFeature} feature permission.` : " no license feature required."
        )
        if (store.getters["auth/hasMeetingPermission"](to.params.id, permission)) {
          log.debug(`Permission check passed: user has ${permission} permission.`)
          next()
        } else {
          log.warn("Missing permission ", permission, ". Redirecting to homepage.")
          next({ name: "home" })
        }
      } else {
        log.warn("Missing license feature ", licenseFeature, ". Redirecting to homepage.")
        next({ name: "home" })
      }
    })
  }
}

const beforeRouteEitherLicenseAndPermissionCheckGuard = function (licenseFeature1, licenseFeature2, permission) {
  return (...[to, , next]) => {
    // Fire license load method to validate if the required license information has been loaded.
    // If loaded proceed with license validation, if not, wait for the license information to be loaded
    Promise.all([ensureLicenseLoaded(), ensureMeetingPermissionsLoaded(to.params.id)]).then(() => {
      const isLicensed =
        (!licenseFeature1 && !licenseFeature2) ||
        isFeatureLicensed(licenseFeature1) ||
        isFeatureLicensed(licenseFeature2)

      if (isLicensed) {
        log.debug(
          "License feature check passed: ",
          licenseFeature1 || licenseFeature2
            ? `user has at least one of ${licenseFeature1} or ${licenseFeature2} feature permissions.`
            : "no license feature required."
        )
        if (store.getters["auth/hasMeetingPermission"](to.params.id, permission)) {
          log.debug(`Permission check passed: user has ${permission} permission.`)
          next()
        } else {
          log.warn("Missing permission ", permission, ". Redirecting to homepage.")
          next({ name: "home" })
        }
      } else {
        log.warn(
          "Missing required license features ",
          licenseFeature1,
          " or ",
          licenseFeature2,
          ". Redirecting to homepage."
        )
        next({ name: "home" })
      }
    })
  }
}
/**
 * guard to redirect unauthenticated users
 *
 * @param to
 * @param from
 * @param next
 * @returns {Promise<any>}
 */
/*
const ifAuthenticated = (to, from, next) => {
  if (store.getters['auth/isAuthenticated']) {
    next()
  }
  else if(localStorage.getItem('authHeader') === null)
    next({
           name: 'login',
           query: { redirect: to.fullPath }
         })
  else
    return new Promise((resolve, reject) => {
      store.dispatch('auth/authVerify', localStorage.getItem('authHeader'))
        .then(resolve => {
          next()
        }, reject => {
          next({
            name: 'login',
            query: { redirect: to.fullPath }
               })
        });
    });
}*/

export {
  ifNotAuthenticated,
  beforeRoutePermissionCheckGuard,
  beforeRouteLicenseCheckGuard,
  beforeRouteLicenseAndPermissionCheckGuard,
  beforeRouteEitherLicenseAndPermissionCheckGuard,
  ensureMeetingPermissionsLoaded,
}
